import {
  Text,
  IconArrowInbox,
  IconEllipsisHorizontal,
  IconFileText,
  Link,
  GreyGrey30,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  GreyWhite,
  Primary14SemiBold,
  Primary13Regular,
  Badge,
  RadioButtonGroup,
  Button,
  IconArrowUpRight,
  SequenceMenu,
  Center,
  Flex
} from '@sequencehq/core-components'
import { useHeader } from './useHeader.tsx'
import { QuoteEditorPreviewProps } from 'modules/Cube/view/layouts/quote/QuoteEditorPreview/QuoteEditorPreview.types'
import { formatQuoteTitle } from 'Cube/utils/formatQuoteTitle'
import { useMemo } from 'react'
import { getPortalDomain } from '@sequencehq/utils'
import { getEnvironment } from 'lib/environment/environment'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

// Constants for reusable values
const HEADER_HEIGHT = '48px'
const TITLE_MAX_WIDTH = '450px'
const CONTAINER_MAX_WIDTH = '60%'

const styles = {
  title: {
    ...Primary14SemiBold,
    color: GreyGrey80
  },
  archivedTitle: {
    ...Primary14SemiBold,
    color: GreyGrey60
  }
}

type Props = {
  hook: QuoteEditorPreviewProps
}

// Extracted component for the title section
type TitleSectionProps = {
  title: string
  isArchived: boolean
  hasUnpublishedChanges: boolean
  isExpired: boolean
  isATemplate: boolean
}

const TitleSection = ({
  title,
  isArchived,
  hasUnpublishedChanges,
  isExpired,
  isATemplate
}: TitleSectionProps) => (
  <Flex
    position="absolute"
    left="50%"
    transform="translateX(-50%)"
    gap="4px"
    alignItems="center"
    maxWidth={CONTAINER_MAX_WIDTH}
  >
    <Text
      {...Primary13Regular}
      color={GreyGrey60}
      isTruncated
      display="flex"
      alignItems="center"
    >
      <Text
        as={Link.Unstyled}
        color="inherit"
        to="/quotes"
        display="inline-block"
      >
        {isATemplate ? 'Quote Templates' : 'Quotes'}
      </Text>
      <Text as="span" mx="4px">
        /
      </Text>
      <Text
        as="span"
        style={isArchived ? styles.archivedTitle : styles.title}
        maxWidth={TITLE_MAX_WIDTH}
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        display="inline-block"
        title={title}
      >
        {title}
      </Text>
    </Text>

    {hasUnpublishedChanges && (
      <Badge sentiment="highlight" size="md" textTransform="none">
        Unpublished changes
      </Badge>
    )}
    {isExpired && (
      <Badge sentiment="error" size="md" textTransform="none">
        Expired
      </Badge>
    )}
  </Flex>
)

export const Header = ({ hook }: Props) => {
  const { downloadPdfButton, downloadDocxButton, viewMode, cubeContext } =
    useHeader(hook)

  const flags = useFeatureFlags()

  const isArchived = cubeContext.queries.rawData.data.common.isArchived

  const title = formatQuoteTitle({
    domainTitle: cubeContext.queries.rawData.data.common.title,
    isArchived
  })

  const hasUnpublishedChanges =
    cubeContext.queries.availableFeatures.quote.deleteLatestDraft.available
      .visible
  const isExpired = cubeContext.queries.rawData.data.quote.isExpired

  const portalUrl = useMemo(() => {
    const { customerId, id } = cubeContext.queries.rawData.data.common
    return `${getPortalDomain(getEnvironment())}/quotes/${customerId}/${id}`
  }, [cubeContext.queries.rawData.data.common])

  const hasBeenPublished = useMemo(
    () => !!cubeContext.queries.rawData.data.quote.publishedAt,
    [cubeContext.queries.rawData.data.quote.publishedAt]
  )

  const isATemplate = cubeContext.queries.quote.isATemplate

  return (
    <Flex
      background={GreyWhite}
      borderBottom={`${GreyGrey30} 1px solid`}
      width="100%"
      alignItems="center"
      justifyContent="space-between"
      px="8px"
      height={HEADER_HEIGHT}
      position="relative"
    >
      <RadioButtonGroup
        options={viewMode.options}
        value={viewMode.current}
        onChange={viewMode.onChange}
        data-testid="preview.quoteEditor.viewMode"
      />

      <Flex gap="8px">
        {!isATemplate && hasBeenPublished && !isArchived && (
          <Button
            variant="secondary"
            onClick={() => window.open(portalUrl, '_blank')}
            leadingIcon={<IconArrowUpRight color="inherit" />}
            data-testid="quote.viewPublishedQuote"
          >
            View published quote
          </Button>
        )}

        <Button
          variant="secondary"
          leadingIcon={<IconArrowInbox color="inherit" />}
          onClick={downloadPdfButton.onClick}
          data-testid="quote.preview.downloadPdf"
        >
          Download PDF
        </Button>

        {flags.useDocxQuoteDownload && (
          <SequenceMenu
            alignment="bottom-right"
            data-testid="quote.preview.downloadOptions.menu"
            closeMenuOnItemClick
            items={[
              <SequenceMenu.Item
                key="download-docx"
                uuid="download-docx"
                label="Microsoft Word (.docx)"
                iconLeft={<IconFileText color="inherit" />}
                onClick={downloadDocxButton.onClick}
                data-testid="quote.preview.menuItem.downloadDocx"
              />
            ]}
            menuButton={
              <Button variant="ghost" size="32px" aria-label="Download options">
                <Center flexShrink="0">
                  <IconEllipsisHorizontal color={GreyGrey70} />
                </Center>
              </Button>
            }
          />
        )}
      </Flex>

      <TitleSection
        title={title}
        isArchived={isArchived}
        hasUnpublishedChanges={hasUnpublishedChanges}
        isExpired={isExpired}
        isATemplate={isATemplate}
      />
    </Flex>
  )
}
